import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode } from "./BrandModeContext";
import { createCampaign, fetchCampaignDetail, updateCampaign, deleteCampaign } from "./services/brandModeServices";
import { fmtRange, fullName } from "./brandUtils";
import {
  AD_USAGE_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  PAYMENT_STRUCTURES,
  PLATFORM_OPTIONS,
  estimateFromRates,
} from "./brandDealUtils";
import iconPlus from "../../assets/brand/icon-plus.svg";
import iconCalendar from "../../assets/brand/icon-calendar.svg";
import {
  Avatar,
  Btn,
  Card,
  ConfirmModal,
  EmptyState,
  Field,
  Input,
  Loading,
  PageHeader,
  SearchBox,
  Select,
  TextArea,
} from "./ui/BrandUI";

const DEFAULT_ROWS = [
  { platform: "Instagram", contentType: "Post", quantity: 3, notes: "Product in authentic context" },
  { platform: "TikTok", contentType: "Video", quantity: 2, notes: "15-60s format" },
  { platform: "Instagram", contentType: "Stories", quantity: 1, notes: "5+ clips over the campaign" },
];

const SectionTitle = ({ n, title, sub }) => (
  <div className="flex flex-col gap-[6px]">
    <div className="flex items-center gap-3">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-bm-ink bm-h-xs text-white">{n}</span>
      <h2 className="bm-h-m uppercase text-bm-ink">{title}</h2>
    </div>
    {sub && <p className="bm-body-m text-bm-muted">{sub}</p>}
  </div>
);

const DateInput = ({ value, onChange }) => (
  <span className="relative flex">
    <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="YYYY-MM-DD" className="pr-10" />
    <img src={iconCalendar} alt="" className="pointer-events-none absolute right-[14px] top-3 h-4 w-4 opacity-70" />
  </span>
);

const CampaignBuilderPage = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [params] = useSearchParams();
  const { pool, athleteById, options, shortlist, refreshCampaigns } = useBrandMode();

  const [loading, setLoading] = useState(!!campaignId);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);
  const [rosterRows, setRosterRows] = useState({}); // athleteId -> row (for athletes not in the pool)
  const [discardOpen, setDiscardOpen] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sport, setSport] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [adUsageDays, setAdUsageDays] = useState(90);
  const [budget, setBudget] = useState({ total: "", perAthleteMin: "", perAthleteMax: "", paymentStructure: "milestone" });
  const [athleteIds, setAthleteIds] = useState([]);
  const [search, setSearch] = useState("");

  // Prefill: existing campaign (edit) or the working shortlist (?fromShortlist=1).
  useEffect(() => {
    let cancelled = false;
    if (campaignId) {
      (async () => {
        try {
          const data = await fetchCampaignDetail(campaignId);
          if (cancelled) return;
          const c = data.campaign;
          setExisting(c);
          setName(c.name || "");
          setDescription(c.description || "");
          setSport(c.sport || "");
          setStartDate(c.startDate || "");
          setEndDate(c.endDate || "");
          setRows(c.deliverablesTemplate?.length ? c.deliverablesTemplate : DEFAULT_ROWS);
          setAdUsageDays(c.adUsageDays || 90);
          setBudget({
            total: c.budget?.total ?? "",
            perAthleteMin: c.budget?.perAthleteMin ?? "",
            perAthleteMax: c.budget?.perAthleteMax ?? "",
            paymentStructure: c.budget?.paymentStructure || "milestone",
          });
          setAthleteIds(c.athleteIds || []);
          const map = {};
          (data.roster || []).forEach((r) => {
            map[r.athleteId] = r.athlete;
          });
          setRosterRows(map);
        } catch (err) {
          toast.error(err.message || "Could not load campaign");
          navigate("/brand/campaigns");
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    } else if (params.get("fromShortlist") === "1" && shortlist.items?.length) {
      setAthleteIds(shortlist.items.map((i) => i.athleteId));
      if (shortlist.targetBudget) setBudget((b) => ({ ...b, total: shortlist.targetBudget }));
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const athleteRow = (id) => athleteById[id] || rosterRows[id] || { athleteId: id, name: "Athlete", surname: "" };

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return pool
      .filter((a) => !athleteIds.includes(a.athleteId))
      .filter((a) => `${a.name} ${a.surname} ${a.school} ${a.sport}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [search, pool, athleteIds]);

  const sports = useMemo(() => {
    const set = new Set(options?.sports || []);
    if (sport) set.add(sport);
    return Array.from(set);
  }, [options, sport]);

  const setRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const addRow = () => setRows((r) => [...r, { platform: "Instagram", contentType: "Post", quantity: 1, notes: "" }]);

  const payload = () => ({
    name: name.trim(),
    description,
    sport,
    startDate,
    endDate,
    deliverablesTemplate: rows,
    adUsageDays,
    budget: {
      total: budget.total === "" ? null : Number(String(budget.total).replace(/,/g, "")),
      perAthleteMin: budget.perAthleteMin === "" ? null : Number(String(budget.perAthleteMin).replace(/,/g, "")),
      perAthleteMax: budget.perAthleteMax === "" ? null : Number(String(budget.perAthleteMax).replace(/,/g, "")),
      paymentStructure: budget.paymentStructure,
    },
    athleteIds,
  });

  const canCreate = name.trim() && startDate && endDate && athleteIds.length > 0 && rows.length > 0;
  const isDraft = !existing || existing.status === "draft";

  const persist = async () => {
    const body = payload();
    if (!body.name) body.name = "Untitled campaign";
    if (existing) {
      await updateCampaign(existing.id, body);
      return existing.id;
    }
    const created = await createCampaign(body);
    return created.id;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await persist();
      await refreshCampaigns();
      toast.success("Draft saved.");
      navigate("/brand/campaigns");
    } catch (err) {
      toast.error(err.message || "Could not save the draft");
    } finally {
      setSaving(false);
    }
  };

  const create = async () => {
    if (!canCreate) return;
    setSaving(true);
    try {
      const id = await persist();
      await refreshCampaigns();
      toast.success(existing ? "Campaign updated." : "Campaign created. Send offers to move it out of draft.");
      navigate(`/brand/campaigns/${id}`);
    } catch (err) {
      toast.error(err.message || "Could not create the campaign");
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    try {
      if (existing && existing.status === "draft") await deleteCampaign(existing.id);
      await refreshCampaigns();
      navigate("/brand/campaigns");
    } catch (err) {
      toast.error(err.message || "Could not discard the draft");
    }
  };

  if (loading) return <Loading label="Loading campaign..." />;

  return (
    <div>
      <PageHeader
        title={existing ? (isDraft ? "Edit Draft" : "Edit Campaign") : "New Campaign"}
        subtitle="Set up your campaign details. You can save as draft and come back anytime."
      />

      <div className="flex flex-col gap-6 pt-3">
        {/* 1. Basics */}
        <Card className="flex flex-col gap-5">
          <SectionTitle n={1} title="Campaign basics" />
          <div className="flex flex-col gap-4">
            <Field label="Campaign Name">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Summer Fit Series" />
            </Field>
            <Field label="Campaign Description">
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of your campaign goals and target audience..."
              />
            </Field>
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Sport / Category">
                <Select value={sport} onChange={(e) => setSport(e.target.value)}>
                  <option value="">Select sport</option>
                  {sports.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Start Date">
                <DateInput value={startDate} onChange={setStartDate} />
              </Field>
              <Field label="End Date">
                <DateInput value={endDate} onChange={setEndDate} />
              </Field>
            </div>
          </div>
        </Card>

        {/* 2. Deliverables template */}
        <Card className="flex flex-col gap-5">
          <SectionTitle n={2} title="Deliverables template" sub="Default deliverables for offers in this campaign. You can customize per athlete." />
          <div className="flex flex-col gap-3">
            <div className="hidden gap-3 px-1 bm-label-xs text-bm-muted md:flex">
              <span className="w-[220px]">Platform</span>
              <span className="w-[220px]">Content type</span>
              <span className="w-[120px]">Quantity</span>
              <span className="flex-1">Notes / requirements</span>
              <span className="w-6" />
            </div>
            {rows.map((row, i) => (
              <div key={i} className="flex flex-wrap items-center gap-3 md:flex-nowrap">
                <Select value={row.platform} onChange={(e) => setRow(i, { platform: e.target.value })} className="!h-9 md:!w-[220px]">
                  {PLATFORM_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </Select>
                <Select value={row.contentType} onChange={(e) => setRow(i, { contentType: e.target.value })} className="!h-9 md:!w-[220px]">
                  {CONTENT_TYPE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </Select>
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={row.quantity}
                  onChange={(e) => setRow(i, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                  className="!h-9 md:!w-[120px]"
                />
                <Input value={row.notes} onChange={(e) => setRow(i, { notes: e.target.value })} placeholder="Notes / requirements" className="!h-9 flex-1" />
                <button type="button" onClick={() => removeRow(i)} aria-label="Remove deliverable" className="w-6 text-center bm-body-m text-bm-tertiary hover:text-bm-critical">
                  ×
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addRow} className="flex items-center gap-[6px] bm-label-xs text-bm-orange">
            <img src={iconPlus} alt="" className="h-[14px] w-[14px]" />
            Add deliverable
          </button>
          <div className="flex items-center gap-2 bm-body-m text-bm-ink">
            Brand may use content in ads for
            <Select value={adUsageDays} onChange={(e) => setAdUsageDays(Number(e.target.value))} className="!h-7 !w-auto !rounded-[4px] !px-[10px] !pr-7 bm-label-xs">
              {AD_USAGE_OPTIONS.map((d) => <option key={d} value={d}>{d} days</option>)}
            </Select>
          </div>
        </Card>

        {/* 3. Budget */}
        <Card className="flex flex-col gap-5">
          <SectionTitle n={3} title="Estimated budget" />
          <div className="flex flex-col gap-4">
            <Field label="Total Campaign Budget">
              <Input prefix="$" inputMode="numeric" value={budget.total} onChange={(e) => setBudget((b) => ({ ...b, total: e.target.value.replace(/[^\d,]/g, "") }))} placeholder="0" />
            </Field>
            <Field label="Suggested Per-Athlete Range">
              <div className="grid gap-4 md:grid-cols-2">
                <Input prefix="Min $" inputMode="numeric" value={budget.perAthleteMin} onChange={(e) => setBudget((b) => ({ ...b, perAthleteMin: e.target.value.replace(/[^\d,]/g, "") }))} placeholder="0" />
                <Input prefix="Max $" inputMode="numeric" value={budget.perAthleteMax} onChange={(e) => setBudget((b) => ({ ...b, perAthleteMax: e.target.value.replace(/[^\d,]/g, "") }))} placeholder="0" />
              </div>
            </Field>
            <Field label="Payment Structure">
              <Select value={budget.paymentStructure} onChange={(e) => setBudget((b) => ({ ...b, paymentStructure: e.target.value }))}>
                {PAYMENT_STRUCTURES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </Select>
            </Field>
            <div className="rounded-[8px] bg-bm-subtle px-[14px] py-[10px] bm-body-s text-bm-muted">
              Cache fees are charged to the brand. Athletes receive 100% of their compensation.
            </div>
          </div>
        </Card>

        {/* 4. Shortlist */}
        <Card className="flex flex-col gap-5">
          <SectionTitle n={4} title="Athlete shortlist" sub="Add athletes from Discover or invite by name. You can also skip this and add athletes later." />
          <div className="relative">
            <SearchBox subtle aria-label="Search athletes to shortlist" value={search} onChange={setSearch} placeholder="Search athletes by name, school, or sport..." />
            {searchResults.length > 0 && (
              <div className="absolute left-0 top-11 z-20 w-full overflow-hidden rounded-[12px] border border-bm-border bg-white shadow-xl">
                {searchResults.map((a) => (
                  <button
                    key={a.athleteId}
                    type="button"
                    onClick={() => {
                      setAthleteIds((ids) => [...ids, a.athleteId]);
                      setSearch("");
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-bm-subtle"
                  >
                    <Avatar name={a.name} surname={a.surname} src={a.avatar} size={28} />
                    <span className="bm-label-l text-bm-ink">{fullName(a)}</span>
                    <span className="ml-auto bm-body-s text-bm-tertiary">{[a.position, a.school].filter(Boolean).join(" · ")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {athleteIds.length ? (
            <div className="flex flex-col divide-y divide-bm-mutedbg rounded-[8px] border border-bm-border">
              {athleteIds.map((id) => {
                const a = athleteRow(id);
                const est = estimateFromRates(a.nilRates, rows);
                return (
                  <div key={id} className="flex items-center gap-3 px-4 py-3">
                    <Avatar name={a.name} surname={a.surname} src={a.avatar} size={32} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate bm-label-l text-bm-ink">{fullName(a)}</span>
                      <span className="truncate bm-body-s text-bm-muted">
                        {[a.position, a.school, a.cacheScore ? `CacheScore ${a.cacheScore}` : null].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="bm-body-m text-bm-muted">{est ? `${fmtRange(est, { compact: false })} est.` : ""}</span>
                    <button type="button" onClick={() => setAthleteIds((ids) => ids.filter((x) => x !== id))} aria-label={`Remove ${fullName(a)}`} className="ml-2 bm-body-m text-bm-tertiary hover:text-bm-critical">
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              action={
                <div className="flex flex-wrap justify-center gap-2">
                  <Btn variant="secondary" size="sm" onClick={() => navigate("/brand/discover")}>+ Add from Discover</Btn>
                  {shortlist.items?.length ? (
                    <Btn variant="secondary" size="sm" onClick={() => setAthleteIds(shortlist.items.map((i) => i.athleteId))}>
                      Import shortlist ({shortlist.items.length})
                    </Btn>
                  ) : null}
                </div>
              }
            >
              No athletes added yet. Browse Discover to find athletes.
            </EmptyState>
          )}
        </Card>

        {/* Action bar */}
        <Card className="flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={() => (existing?.status === "draft" ? setDiscardOpen(true) : navigate("/brand/campaigns"))} className="bm-label-l text-bm-muted hover:text-bm-ink">
            {existing?.status === "draft" ? "Discard draft" : "Cancel"}
          </button>
          <div className="flex items-center gap-3">
            {isDraft && (
              <Btn variant="secondary" onClick={saveDraft} disabled={saving}>
                Save as draft
              </Btn>
            )}
            <Btn onClick={create} disabled={!canCreate || saving}>
              {saving ? "Saving..." : existing ? (isDraft ? "Create campaign" : "Save changes") : "Create campaign"}
            </Btn>
          </div>
        </Card>
      </div>

      {discardOpen && (
        <ConfirmModal
          title="Discard this draft?"
          body="The draft, its deliverables template and shortlist will be deleted. Athletes were never contacted, so nothing is sent."
          ackLabel="I understand the draft will be deleted."
          confirmLabel="Discard draft"
          tone="danger"
          onConfirm={discard}
          onClose={() => setDiscardOpen(false)}
        />
      )}
    </div>
  );
};

export default CampaignBuilderPage;
